import useApi from '../utils/axios-pettitions';
import { useAppSelector } from '../contexts/hooks';
import { newToast } from '../components';
import {
  MedicionQcDTO,
  CreateMedicionQcDTO,
  UpdateMedicionQcDTO,
  ValidateMedicionDTO,
  ViolacionQcDTO,
  ResolveViolacionDTO,
  LeveyJenningsDataDTO,
  SigmaMetricsDTO,
  QcReportFilterParams,
  SigmaPerformanceReportDTO,
  CvPerformanceReportDTO,
  GeneralInfoRowDTO,
  IndicatorAnalitoDTO,
  SigmaHistoryRowDTO,
  CvHistoryRowDTO,
  QcPlanningRowDTO,
  ConsolidatedReportDTO,
  LeveyJenningsReportRowDTO,
  FiltroMedicionesQcDTO,
} from '../types/types.qualityControl';

export const useApiMeasurementsQC = () => {
  const { runApi } = useApi();
  const userId = useAppSelector((state) => state.userInfo.User?.Us_Id);

  //#region Mediciones

  const registerMeasurement = async (medicion: Omit<CreateMedicionQcDTO, 'Qcme_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: CreateMedicionQcDTO = {
      ...medicion,
      Qcme_Us_Id: userId,
    };

    return runApi('qualitymeasurements/registerMeasurement', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 105) {
        newToast('SUCCESS', res.message, 3);
        return res.data as MedicionQcDTO;
      }
      return null;
    });
  };

  const updateMeasurement = async (medicion: Omit<UpdateMedicionQcDTO, 'Qcme_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: UpdateMedicionQcDTO = {
      ...medicion,
      Qcme_Us_Id: userId,
    };



    return runApi('qualitymeasurements/updateMeasurement', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {

        return null;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return res.data as MedicionQcDTO;
      }
      return null;
    });
  };

  // Edición de una medición de días anteriores (conserva la fecha original).
  // Gobernado por el permiso especial Prm_QC_EditLater (el backend devuelve 202 si no lo tiene).
  const updateMeasurementLate = async (medicion: Omit<UpdateMedicionQcDTO, 'Qcme_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: UpdateMedicionQcDTO = {
      ...medicion,
      Qcme_Us_Id: userId,
    };

    return runApi('qualitymeasurements/updateMeasurementLate', 'POST', null, data).then((res) => {
      if (res.internalCode === 107) {
        newToast('SUCCESS', res.message || 'Medición modificada correctamente', 3);
        return res.data as MedicionQcDTO;
      }
      return null;
    });
  };

  const getMeasurementById = async (id: number) => {
    return runApi('qualitymeasurements/getMeasurementById', 'GET', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as MedicionQcDTO;
      }
      return null;
    });
  };

  const getMeasurementsByConfiguration = async (configuracionId: number, cantidad: number = 20) => {
    return runApi('qualitymeasurements/getMeasurementsByConfiguration', 'GET', { configuracionId, cantidad }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as MedicionQcDTO[];
      }
      return [];
    });
  };

  // Mediciones dentro de un rango de fechas, con filtros opcionales por analito/sede/analizador.
  // El backend normaliza el rango a día completo y ordena por fecha descendente (tope 2000).
  const getMeasurementsByDateRange = async (filtro: FiltroMedicionesQcDTO) => {
    const params: any = {
      fechaDesde: filtro.fechaDesde,
      fechaHasta: filtro.fechaHasta,
    };
    if (filtro.analitoId != null) params.analitoId = filtro.analitoId;
    if (filtro.sedeId != null) params.sedeId = filtro.sedeId;
    if (filtro.analizadorId != null) params.analizadorId = filtro.analizadorId;

    return runApi('qualitymeasurements/getMeasurementsByDateRange', 'GET', params, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as MedicionQcDTO[];
      }
      return [];
    });
  };

  const getPendingReviewMeasurements = async (sedeId?: number, analitoId?: number) => {
    return runApi('qualitymeasurements/getPendingReviewMeasurements', 'GET', { sedeId, analitoId }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as MedicionQcDTO[];
      }
      return [];
    });
  };

  //#endregion

  //#region Validación Manual

  const validateMeasurement = async (id: number, comentario?: string) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return false;
    }

    const data: ValidateMedicionDTO = {
      Qcme_ComentarioValidacion: comentario,
      Qcme_UsValidacion_Id: userId,
    };

    return runApi('qualitymeasurements/validateMeasurement', 'POST', { id }, data).then((res) => {
      if (res.internalCode >= 200) {
        return false;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  const rejectMeasurement = async (id: number, comentario?: string) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return false;
    }

    const data: ValidateMedicionDTO = {
      Qcme_ComentarioValidacion: comentario,
      Qcme_UsValidacion_Id: userId,
    };

    return runApi('qualitymeasurements/rejectMeasurement', 'POST', { id }, data).then((res) => {
      if (res.internalCode >= 200) {
        return false;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Violaciones Westgard

  const getViolationsByMeasurement = async (medicionId: number) => {
    return runApi('qualitymeasurements/getViolationsByMeasurement', 'GET', { medicionId }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ViolacionQcDTO[];
      }
      return [];
    });
  };

  const getPendingViolations = async (sedeId?: number) => {
    return runApi('qualitymeasurements/getPendingViolations', 'GET', { sedeId }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ViolacionQcDTO[];
      }
      return [];
    });
  };

  const resolveViolation = async (id: number, accion: string) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return false;
    }

    const data: ResolveViolacionDTO = {
      Qcv_Accion: accion,
      Qcv_Us_Id: userId,
    };

    return runApi('qualitymeasurements/resolveViolation', 'POST', { id }, data).then((res) => {
      if (res.internalCode >= 200) {
        return false;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Gráficos

  const getLeveyJenningsData = async (configuracionId: number) => {
    return runApi('qualitymeasurements/getLeveyJenningsData', 'GET', { configuracionId }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as LeveyJenningsDataDTO;
      }
      return null;
    });
  };

  const getSigmaMetrics = async (configuracionId: number, silentOnNoData: boolean = false) => {
    return runApi('qualitymeasurements/getSigmaMetrics', 'GET', { configuracionId }, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as SigmaMetricsDTO;
      }
      if (res.internalCode === 209) {
        if (!silentOnNoData) {
          newToast('INFO', res.message || 'La configuración no es cuantitativa o no tiene TEa definido', 4);
        }
        return null;
      }
      if (res.internalCode >= 200) {
        return null;
      }
      return null;
    });
  };

  //#endregion

  //#region Informes Estadísticos (Six Sigma)

  const buildReportParams = (filter: QcReportFilterParams) => {
    const params: any = {
      FechaDesde: filter.FechaDesde,
      FechaHasta: filter.FechaHasta,
    };
    if (filter.SedeId != null) params.SedeId = filter.SedeId;
    if (filter.AnalizadorId != null) params.AnalizadorId = filter.AnalizadorId;
    if (filter.MaterialId != null) params.MaterialId = filter.MaterialId;
    if (filter.LoteId != null) params.LoteId = filter.LoteId;
    if (filter.AnalitoId != null) params.AnalitoId = filter.AnalitoId;
    return params;
  };

  const getSigmaPerformance = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/sigmaPerformance', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as SigmaPerformanceReportDTO;
      return null;
    });
  };

  const getCvPerformance = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/cvPerformance', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as CvPerformanceReportDTO;
      return null;
    });
  };

  const getGeneralInfo = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/generalInfo', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as GeneralInfoRowDTO[];
      return [] as GeneralInfoRowDTO[];
    });
  };

  const getIndicators = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/indicators', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as IndicatorAnalitoDTO[];
      return [] as IndicatorAnalitoDTO[];
    });
  };

  const getSigmaHistory = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/sigmaHistory', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as SigmaHistoryRowDTO[];
      return [] as SigmaHistoryRowDTO[];
    });
  };

  const getCvHistory = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/cvHistory', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as CvHistoryRowDTO[];
      return [] as CvHistoryRowDTO[];
    });
  };

  const getQcPlanning = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/qcPlanning', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as QcPlanningRowDTO[];
      return [] as QcPlanningRowDTO[];
    });
  };

  const getConsolidatedReport = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/consolidated', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as ConsolidatedReportDTO;
      return null;
    });
  };

  const getLeveyJenningsReport = async (filter: QcReportFilterParams) => {
    return runApi('qualityreports/leveyJennings', 'GET', buildReportParams(filter), null).then((res) => {
      if (res.internalCode === 101) return res.data as LeveyJenningsReportRowDTO[];
      return [] as LeveyJenningsReportRowDTO[];
    });
  };

  //#endregion

  return {
    registerMeasurement,
    updateMeasurement,
    updateMeasurementLate,
    getMeasurementById,
    getMeasurementsByConfiguration,
    getMeasurementsByDateRange,
    getPendingReviewMeasurements,
    validateMeasurement,
    rejectMeasurement,
    getViolationsByMeasurement,
    getPendingViolations,
    resolveViolation,
    getLeveyJenningsData,
    getSigmaMetrics,
    getSigmaPerformance,
    getCvPerformance,
    getGeneralInfo,
    getIndicators,
    getSigmaHistory,
    getCvHistory,
    getQcPlanning,
    getConsolidatedReport,
    getLeveyJenningsReport,
  };
};
